import { useState } from 'react';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import { reportsApi } from '@/services/api';
import { downloadCsv } from '@/utils/csv';
import PurchaseReportFilters from '../shared/PurchaseReportFilters';
import ReportDataTable, { columnsToCsvHeaders, rowsToCsvData } from '../shared/ReportDataTable';
import ReportLayout from '../shared/ReportLayout';
import ReportSummaryCards from '../shared/ReportSummaryCards';
import { fetchPurchaseReportExport, usePurchaseReport } from '../shared/usePurchaseReport';
import type { PurchaseSummaryRow } from './columns';
import { purchaseSummaryColumns } from './columns';
import { formatMoney } from '@/utils/currency';

export default function PurchaseSummaryPage() {
  const dateRangeHook = useReportDateRange('This Month');
  const { from_date, to_date } = dateRangeHook;
  const [supplier, setSupplier] = useState('');
  const [supplierLabel, setSupplierLabel] = useState('');
  const [status, setStatus] = useState('');

  const params = {
    from_date,
    to_date,
    supplier: supplier || undefined,
    status: status || undefined,
  };

  const { rows, total, page, setPage, pageSize, summary, isLoading } = usePurchaseReport({
    queryKey: 'purchaseSummary',
    params,
    fetcher: (p) => reportsApi.getPurchaseSummary(p),
  });

  const handleExport = async () => {
    const data = await fetchPurchaseReportExport(reportsApi.getPurchaseSummary, params);
    downloadCsv(
      `purchase-summary-${from_date}-${to_date}.csv`,
      columnsToCsvHeaders(purchaseSummaryColumns),
      rowsToCsvData(data.rows as unknown as PurchaseSummaryRow[], purchaseSummaryColumns),
    );
  };

  const summaryCards = [
    { label: 'Total Purchase Bills', value: Number(summary.total_purchase_bills ?? 0) },
    { label: 'Gross Purchase', value: `${formatMoney(summary.gross_purchase as number)}` },
    { label: 'Total Paid', value: `${formatMoney(summary.total_paid as number)}`, tone: 'success' as const },
    { label: 'Outstanding', value: `${formatMoney(summary.outstanding as number)}`, tone: 'warning' as const },
    {
      label: 'Purchase Return Amount',
      value: `${formatMoney(summary.purchase_return_amount as number)}`,
      tone: 'danger' as const,
    },
  ];

  return (
    <ReportLayout
      onExport={handleExport}
      exportDisabled={!total}
      isLoading={isLoading}
      isEmpty={!rows.length}
      page={page}
      total={total}
      pageSize={pageSize}
      onPageChange={setPage}
      filters={
        <PurchaseReportFilters
          dateRangeHook={dateRangeHook}
          supplier={supplier}
          supplierLabel={supplierLabel}
          onSupplierChange={(id, label) => {
            setSupplier(id);
            setSupplierLabel(label);
          }}
          status={status}
          onStatusChange={setStatus}
          showStatus
        />
      }
      summary={<ReportSummaryCards items={summaryCards} columns={5} />}
    >
      <ReportDataTable data={rows as never[]} columns={purchaseSummaryColumns} />
    </ReportLayout>
  );
}
